import { TextInput } from '@mantine/core'
import { ObjectBidParamKeyRename } from '@pubstack/common/src/bidder-seat'
import { FunctionComponent, useState } from 'react'
import Button from '~/components/Button'
import { label, prefix } from '~/components/layout/ProductTrackingIntegration'
import { Modal } from '~/components/Modal'

type Props = {
  /** Full column names, like `ext.customKey`. Empty when the save only renames. */
  removed: string[]
  renamed: ObjectBidParamKeyRename[]
  /** Keys that keep their name but change type, so their values are cleared. */
  reset: string[]
  trackingLabel: `${prefix}-${label}`
  onCancel: () => void
  onConfirm: () => void | Promise<void>
}

/**
 * Confirm a save that renames a mapping column or removes it.
 *
 * A rename is only listed: the values follow the key, so renaming back undoes it. A removal cannot be
 * undone, because every value held under that key is deleted, so it asks for the column name to be typed.
 * That is the one thing a mis-click cannot produce.
 */
export const ConfirmObjectBidParamsModal: FunctionComponent<Props> = ({ removed, renamed, reset, trackingLabel, onCancel, onConfirm }) => {
  const [typed, setTyped] = useState('')
  const [isLoading, setIsLoading] = useState(false)

  const expected = removed.join(', ')
  const canConfirm = removed.length === 0 || typed.trim() === expected
  const destroys = removed.length > 0 || reset.length > 0

  const handleConfirm = async () => {
    if (isLoading || !canConfirm) return
    setIsLoading(true)
    await onConfirm()
  }

  return (
    <Modal.Content>
      <Modal.Title>{destroys ? 'This clears mapping values' : 'Rename custom keys'}</Modal.Title>
      <Modal.Body>
        <div className="flex flex-col gap-3">
          {renamed.length > 0 && (
            <div className="flex flex-col gap-1">
              <span>{renamed.length === 1 ? 'This key is renamed. Its values move with it:' : 'These keys are renamed. Their values move with them:'}</span>
              <ul className="m-0 pl-5">
                {renamed.map(({ namespace, from, to }) => (
                  <li key={`${namespace}.${from}`}>
                    <code>
                      {namespace}.{from}
                    </code>{' '}
                    →{' '}
                    <code>
                      {namespace}.{to}
                    </code>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {reset.length > 0 && (
            <div className="flex flex-col gap-1">
              <span>
                {reset.length === 1 ? 'This key changed type, so the values already filled in for it are cleared.' : 'These keys changed type, so the values already filled in for them are cleared.'}{' '}
                What is there was entered under the old type, and keeping it would send the bidder params that no longer match.
              </span>
              <ul className="m-0 pl-5">
                {reset.map((name) => (
                  <li key={name}>
                    <code>{name}</code>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {removed.length > 0 && (
            <div className="flex flex-col gap-2">
              <span>
                {removed.length === 1
                  ? 'This column is removed from the mapping, and every value already filled in for it is deleted.'
                  : 'These columns are removed from the mapping, and every value already filled in for them is deleted.'}{' '}
                This can&apos;t be undone.
              </span>
              <ul className="m-0 pl-5">
                {removed.map((name) => (
                  <li key={name}>
                    <code>{name}</code>
                  </li>
                ))}
              </ul>
              <TextInput
                label={
                  <>
                    Type <code>{expected}</code> to confirm
                  </>
                }
                value={typed}
                autoComplete="off"
                onChange={(event) => setTyped(event.currentTarget.value)}
              />
            </div>
          )}
        </div>
      </Modal.Body>
      <Modal.Actions>
        <Button variant="tertiary" onClick={onCancel} disabled={isLoading} trackingLabel={`${trackingLabel}-cancel`}>
          Cancel
        </Button>

        <Button
          variant={destroys ? 'negative' : 'primary'}
          onClick={handleConfirm}
          disabled={isLoading || !canConfirm}
          iconName={isLoading ? 'pubstack' : undefined}
          iconSpin={isLoading}
          trackingLabel={`${trackingLabel}-apply`}
        >
          {removed.length > 0 ? 'Delete' : 'Confirm'}
        </Button>
      </Modal.Actions>
    </Modal.Content>
  )
}
